<template>
  <div>
    <slot
        name="prepend"
        v-bind:serviceImplementation="serviceImplementation"
        v-bind:processImplementation="processImplementation"
        v-bind:dataImplementation="dataImplementation">
    </slot>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         サービス実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <v-col cols="6">
        <text-field-like
            :value="serviceImplementation.serviceComponentModel && serviceImplementation.serviceComponentModel.name"
            :value-id="serviceImplementation.id"
            label="サービス実装"
            hide-details>
        </text-field-like>
      </v-col>
      <v-col cols="6">
        <local-id-field
            label="サービス実装 内部ID"
            :value="serviceImplementation.localId"
            hide-details>
        </local-id-field>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         プロセス実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <v-col cols="6">
        <text-field-like
            :value="processImplementation.processComponentModel && processImplementation.processComponentModel.name"
            :value-id="processImplementation.id"
            label="プロセス実装"
            hint="プロセス実装の参照するプロセス構成モデル名"
            hide-details>
        </text-field-like>
      </v-col>
      <v-col cols="6">
        <local-id-field
            label="プロセス実装 内部ID"
            :value="processImplementation.localId"
            hide-details>
        </local-id-field>
      </v-col>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         データ実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <template v-if="readonly">
        <v-col cols="6">
          <text-field-like
              :value="dataImplementation.dataComponentModel && dataImplementation.dataComponentModel.name"
              :value-id="dataImplementation.id"
              label="データ実装"
              hint="プロセス実装の参照するデータ構成モデル名"
              hide-details>
          </text-field-like>
        </v-col>
        <v-col cols="6">
          <local-id-field
              label="データ実装 内部ID"
              :value="dataImplementation.localId"
              hide-details>
          </local-id-field>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12">
          <v-select
              v-model="selectedDataImplementationId"
              :items="dataImplementationItems"
              label="データ実装"
              :readonly="readonly">
            <template #item="{item, attrs, on}">
              <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover">
                <v-col class="name">
                  <menu-inline>
                    <text-field-like
                        label="データ構成モデル名"
                        :value="item.dataComponentModel.name"
                        :value-id="item.id">
                    </text-field-like>
                  </menu-inline>
                </v-col>
                <v-col>
                  <menu-inline>
                    <local-id-field
                        label="内部ID"
                        :value="item.localId">
                    </local-id-field>
                  </menu-inline>
                </v-col>
              </v-list-item>
            </template>
            <template #selection="{item}">
              <div style="display: inline-flex; justify-content: space-between; width: 100%;">
                <text-field-like
                    :value="item.dataComponentModel.name"
                    :value-id="item.id"
                    hide-label
                    hide-details>
                </text-field-like>
                <div style="font-size: 80%; align-self: center;">内部ID: {{ item.localId }}</div>
              </div>
            </template>
          </v-select>
        </v-col>
      </template>
    </v-row>

    <slot
        name="append"
        v-bind:serviceImplementation="serviceImplementation"
        v-bind:processImplementation="processImplementation"
        v-bind:dataImplementation="dataImplementation">
    </slot>
  </div>
</template>

<script>
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import LocalIdField from "@/components/molecules/LocalIdField.vue";
import MenuInline from "@/components/atoms/MenuInline.vue";

export default {
  name: 'DataImplementationSelectForm',
  components: {MenuInline, LocalIdField, TextFieldLike},
  model: {
    prop: 'dataImplementationId',
    event: 'change'
  },
  inject: {
    form: {default: null}
  },
  props: {
    dataImplementationId: {
      type: String,
      default: null
    },
    serviceImplementation: {
      type: Object,
      required: true
    },
    processImplementation: {
      type: Object,
      required: true
    },
    dataImplementations: {
      type: Array,
      default: () => ([])
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedDataImplementationId: null,
    }
  },
  created() {
    this.form && this.form.register(this);
  },
  beforeDestroy() {
    this.form && this.form.unregister(this);
  },
  computed: {
    dataImplementationItems() {
      return this.dataImplementations
          .map(di => ({ value: di.id, ...di }));
    },
    dataImplementation() {
      if(this.dataImplementationId) {
        return this.dataImplementations.find(di => di.id === this.dataImplementationId) ?? null;
      } else {
        return null;
      }
    },
    hasError() {
      return !this.selectedDataImplementationId;
    }
  },
  watch: {
    dataImplementationId: {
      immediate: true,
      handler(dataImplementationId) {
        this.selectedDataImplementationId = dataImplementationId;
      }
    },
    selectedDataImplementationId: {
      handler(selectedDataImplementationId) {
        this.$emit('change', selectedDataImplementationId);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.col.name {
  max-width: 200px;
}
</style>
